import { END_POINTS } from '@config';
import ApiTags from '@constants/api-tags';
import { api } from '@core/baseApi';
import {
  HTTPResponse,
  OrgAndBranchId,
  OrgBranchMemberIds,
  TimestampQueryParams
} from '@types';

import {
  AttendanceGroupListApiResponse,
  AttendanceReportQueryParams,
  AttendanceSummaryApiResponse,
  CreateMemberRequest,
  MemberAttendanceListQueryParams,
  UpdateMemberRequest
} from '../types';

const extendedBaseQuery = api.injectEndpoints({
  endpoints: (builder) => ({
    createMember: builder.mutation<HTTPResponse<any>, CreateMemberRequest>({
      query: (req) => ({
        url: END_POINTS.MEMBER_MANAGEMENT.CREATE_MEMBER(req.params),
        method: 'POST',
        body: req.body
      })
    }),
    updateUserProfile: builder.mutation<HTTPResponse<any>, UpdateMemberRequest>(
      {
        query: (req) => ({
          url: END_POINTS.MEMBER_MANAGEMENT.UPDATE_MEMBER(req.params),
          method: 'POST',
          body: req.body
        })
      }
    ),
    getMemberDetails: builder.query<HTTPResponse<any>, OrgBranchMemberIds>({
      query: ({ organizationId, branchId, memberId }) => ({
        url: `${END_POINTS.MEMBER_MANAGEMENT.GET_MEMBER_DETAILS({
          organizationId,
          branchId,
          memberId
        })}`,
        method: 'GET'
      }),
      providesTags: [ApiTags.BRANCH_TAGS]
    }),
    getMembers: builder.query<
      HTTPResponse<any>,
      { organizationId: string; branchId: string; queryString: string }
    >({
      query: ({ organizationId, branchId, queryString }) => ({
        url: `${END_POINTS.MEMBER_MANAGEMENT.GET_MEMBERS({
          organizationId,
          branchId
        })}${queryString}`,
        method: 'GET'
      }),
      providesTags: [ApiTags.BRANCH_TAGS]
    }),
    getMemberCount: builder.mutation<
      HTTPResponse<any>,
      {
        organizationId: string;
        branchId: string;
        body: {
          group_ids: string[];
          include_non_group_members: boolean;
        };
      }
    >({
      query: ({ organizationId, branchId, body }) => ({
        url: `${END_POINTS.MEMBER_MANAGEMENT.GET_MEMBER_COUNT({
          organizationId,
          branchId
        })}`,
        method: 'POST',
        body
      })
    }),
    deleteMember: builder.mutation<HTTPResponse<any>, OrgBranchMemberIds>({
      query: ({ organizationId, branchId, memberId }) => ({
        url: END_POINTS.MEMBER_MANAGEMENT.DELETE_MEMBER({
          organizationId,
          branchId,
          memberId
        }),
        method: 'DELETE'
      })
    }),
    getMemberAttendanceSummary: builder.query<
      HTTPResponse<AttendanceSummaryApiResponse>,
      {
        params: OrgBranchMemberIds;
        queryParams: TimestampQueryParams & {
          include_attendance_disabled_groups?: boolean;
        };
      }
    >({
      query: ({ params, queryParams }) => ({
        url: END_POINTS.MEMBER_MANAGEMENT.GET_MEMBER_ATTENDANCE_SUMMARY(params),
        method: 'GET',
        params: queryParams
      })
    }),
    getMemberAttendance: builder.query<
      HTTPResponse<AttendanceGroupListApiResponse>,
      {
        params: OrgBranchMemberIds;
        queryParams: MemberAttendanceListQueryParams;
      }
    >({
      query: ({ params, queryParams }) => ({
        url: END_POINTS.MEMBER_MANAGEMENT.GET_MEMBER_ATTENDANCE(params),
        method: 'GET',
        params: queryParams
      })
    }),
    bulkDeleteMembers: builder.mutation<
      { job_id: string },
      OrgAndBranchId & { memberIds: string[] }
    >({
      query: ({ memberIds, ...params }) => ({
        url: END_POINTS.MEMBER_MANAGEMENT.BULK_DELETE_MEMBERS(params),
        method: 'POST',
        body: {
          member_ids: memberIds
        }
      })
    }),
    downloadMemberAttendanceReport: builder.query<
      Blob,
      {
        params: OrgBranchMemberIds;
        queryParams: AttendanceReportQueryParams;
      }
    >({
      query: (req) => ({
        url: END_POINTS.MEMBER_MANAGEMENT.DOWNLOAD_MEMBER_ATTENDANCE_REPORT(
          req.params
        ),
        method: 'GET',
        params: req.queryParams,
        responseHandler: (res) => res.blob()
      })
    }),
    downloadBranchAttendanceReport: builder.query<
      Blob,
      { params: OrgAndBranchId; queryParams: AttendanceReportQueryParams }
    >({
      query: (req) => ({
        url: END_POINTS.MEMBER_MANAGEMENT.DOWNLOAD_BRANCH_ATTENDANCE_REPORT(
          req.params
        ),
        method: 'GET',
        params: req.queryParams,
        responseHandler: (res) => res.blob()
      })
    })
  })
});

export const {
  useCreateMemberMutation,
  useLazyGetMemberDetailsQuery,
  useLazyGetMembersQuery,
  useGetMemberDetailsQuery,
  useGetMemberCountMutation,
  useUpdateUserProfileMutation,
  useDeleteMemberMutation,
  useBulkDeleteMembersMutation,
  useGetMemberAttendanceSummaryQuery,
  useGetMemberAttendanceQuery,
  useLazyGetMemberAttendanceSummaryQuery,
  useLazyGetMemberAttendanceQuery,
  useLazyDownloadMemberAttendanceReportQuery,
  useLazyDownloadBranchAttendanceReportQuery
} = extendedBaseQuery;
